/**
 * Module-scope auth snapshot + ready promise, shared between route
 * `beforeLoad` gates (which run outside React) and the `AuthBridge`
 * component in `src/app-router.tsx` (which mirrors `useConvexAuth()` into
 * this module and resolves `authReady` once the initial check settles).
 */

export type RouterAuthContext = { isLoading: boolean; isAuthenticated: boolean }

let currentAuth: RouterAuthContext = { isAuthenticated: false, isLoading: true }
let resolveAuthReady: (() => void) | null = null
const authReady = new Promise<void>((resolve) => {
  resolveAuthReady = resolve
})

// Set when the client booted the Convex-free marketing router (see
// `src/router.tsx`): no AuthBridge will ever mount, so `authReady` would
// never resolve. Any code path that lands here is a stray in-router
// navigation toward the app — the marketing router's navigation guard is
// already converting it into a full-page load, so freezing the transition
// (rather than resolving "unauthenticated" and bouncing to /sign-in) is
// the correct behavior while the page unloads.
let marketingBoot = false

export const markMarketingBoot = () => {
  marketingBoot = true
}

export const isMarketingBoot = () => marketingBoot

export const ensureAuth = async (): Promise<RouterAuthContext> => {
  // On the server (SSR / prerender) there is no Convex client and no
  // `AuthBridge` to resolve `authReady`, so awaiting it would hang the build.
  // Treat server renders as unauthenticated — the client will re-evaluate the
  // gate after hydration via `router.invalidate()` in AuthBridge.
  if (typeof window === 'undefined') {
    return { isAuthenticated: false, isLoading: false }
  }
  if (marketingBoot) {
    return new Promise<never>(() => {
      // Intentionally never resolves — see the marketingBoot comment above.
    })
  }
  if (currentAuth.isLoading) {
    await authReady
  }
  return currentAuth
}

/**
 * Publish the latest auth verdict from React. Returns the previous snapshot
 * so `AuthBridge` can decide whether to invalidate the router.
 */
export const publishAuth = (auth: RouterAuthContext): RouterAuthContext => {
  const prev = currentAuth
  currentAuth = auth
  if (!auth.isLoading && resolveAuthReady) {
    resolveAuthReady()
    resolveAuthReady = null
  }
  return prev
}

// One-shot flag set right before `signOut()` so the auth gate knows the next
// redirect to `/sign-in` is an intentional sign-out, not a blocked access
// attempt — and therefore should not carry a `?redirect=` param.
let suppressNextRedirectParam = false

export const markSignOutInProgress = () => {
  suppressNextRedirectParam = true
}

export const consumeSignOutInProgress = (): boolean => {
  const wasSet = suppressNextRedirectParam
  suppressNextRedirectParam = false
  return wasSet
}

/**
 * Latch raised across a password sign-up, read by `/sign-up`'s own gate.
 *
 * That gate sends authenticated visitors away, which is right for someone
 * who wanders back to the form — and wrong for the account being created
 * right now, because the onboarding questions run in that route rather than
 * one of their own. The questions mark themselves in the URL (`step=setup`)
 * and the gate honours that, but the URL cannot be the whole answer: the
 * token lands INSIDE `signIn()`, `AuthBridge` invalidates every route the
 * moment it does, and that re-runs the gate against the old location — the
 * one without the param — before the form has had a chance to set it. The
 * redirect wins the race, and the account lands in the app instead of in the
 * questions.
 *
 * So the latch covers the window the URL cannot: raised before `signIn`,
 * lowered once the questions are on screen or the attempt has failed.
 */
let signingUp = false

export const markSignUpInProgress = () => {
  signingUp = true
}

export const isSignUpInProgress = (): boolean => signingUp

export const endSignUp = () => {
  signingUp = false
}

/**
 * Latch raised for the duration of a sign-out, observed by the
 * `_authenticated` gate so the authed tree comes down BEFORE the token is
 * cleared. Shared module-scope state rather than context because the raiser
 * (the account menu) and the observer (the route gate) sit on opposite sides
 * of the tree — the same reason `currentAuth` above lives here.
 *
 * Why the ordering matters: `@convex-dev/react-query` keeps a Convex
 * subscription open until the TanStack query is garbage-collected, NOT until
 * the component unmounts — its cache listener treats `observerRemoved` as a
 * no-op and waits for the `removed` event that `gcTime` eventually triggers.
 * So at the instant `signOut()` nulls the token, every query the user had
 * open is still subscribed; the Convex client re-issues all of them
 * unauthenticated and each throws `Not authenticated` server-side. That is
 * the burst of identical errors this latch exists to prevent.
 *
 * Unmounting removes the observers, which then lets a `queryClient.clear()`
 * actually drop the watches (`clear()` fires `removed` synchronously) with
 * nothing left to re-subscribe. Clearing first without unmounting does not
 * work: the still-mounted observers re-add their queries immediately.
 */
let signingOut = false
const signingOutListeners = new Set<() => void>()

export const subscribeSigningOut = (onChange: () => void): (() => void) => {
  signingOutListeners.add(onChange)
  return () => {
    signingOutListeners.delete(onChange)
  }
}

export const getSigningOut = (): boolean => signingOut

const setSigningOut = (next: boolean) => {
  if (signingOut === next) {
    return
  }
  signingOut = next
  for (const listener of signingOutListeners) {
    listener()
  }
}

/** Raise the latch — the gate unmounts the authed tree and finishes the job. */
export const beginSignOut = () => setSigningOut(true)

/**
 * The impersonation twin of the sign-out latch: raised after
 * `impersonation.start`/`stop` resolves, observed by BOTH shell gates
 * (`_authenticated` and `/admin` — exactly one is mounted at a time), and
 * finished by `useIdentitySwitchTeardown` in `lib/impersonation.ts`.
 *
 * The ordering is load-bearing for the same reason as sign-out: the server
 * has already switched the effective identity the moment the mutation
 * resolves, so every still-mounted subscription is about to recompute as the
 * OTHER user, and workspace queries throw for a caller who is no longer a
 * member. Unmounting the tree first removes the observers, so the subsequent
 * `queryClient.clear()` retires the subscriptions with nothing left to
 * refetch, and only then does the hard navigation load the destination as the
 * new identity.
 */
let identitySwitch: { destination: string } | null = null
const identitySwitchListeners = new Set<() => void>()

export const subscribeIdentitySwitch = (onChange: () => void): (() => void) => {
  identitySwitchListeners.add(onChange)
  return () => {
    identitySwitchListeners.delete(onChange)
  }
}

export const getIdentitySwitch = (): { destination: string } | null => identitySwitch

/** Raise the latch; the mounted shell gate unmounts its tree and finishes. */
export const beginIdentitySwitch = (destination: string) => {
  identitySwitch = { destination }
  for (const listener of identitySwitchListeners) {
    listener()
  }
}

/**
 * Lower the latch. Called when auth settles back to authenticated (a fresh
 * sign-in on the same document) and when the teardown itself fails, so a
 * failed sign-out cannot leave the gate rendering nothing forever.
 */
export const endSignOut = () => setSigningOut(false)
